import { useEffect, useRef, type RefObject } from 'react';

/**
 * Hook that detects clicks outside of the referenced element
 * @param callback Function to call when a click outside is detected
 * @returns Ref to attach to the element
 */
export function useClickOutside<T extends HTMLElement>(
  callback: () => void
): RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on cleanup
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [callback]);

  return ref;
}

/**
 * Hook that detects clicks outside and Escape key press
 * @param callback Function to call when closing is triggered
 * @returns Ref to attach to the element
 */
export function useClickOutsideOrEscape<T extends HTMLElement>(
  callback: () => void,
  isOpen: boolean
): RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        callback();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [callback, isOpen]);

  return ref;
}
